import React from 'react';
import { Check } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import { Button } from '../ui/button';
import { trackCtaClicked } from '../../utils/analytics';

const scrollToCTA = () =>
  document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' });

const GREEN = 'var(--scribane-green)';
const GRAFITO = '#272729';

function BoldInline({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <span>
      {parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**')
          ? <strong key={i}>{part.slice(2, -2)}</strong>
          : part
      )}
    </span>
  );
}

export const Community: React.FC = () => {
  const { t } = useLocale();

  const features = [
    'landing_community_feat_1',
    'landing_community_feat_2',
    'landing_community_feat_3',
    'landing_community_feat_4',
    'landing_community_feat_5',
  ] as const;

  return (
    <section
      id="chapter-zero"
      className="scroll-mt-24"
      style={{ background: '#E9E9EA', padding: '110px 0 120px' }}
    >
      <div style={{ width: '100%', maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>

        {/* Header */}
        <div style={{ maxWidth: '760px', marginBottom: '52px' }}>
          <p style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: '16px',
            lineHeight: '20px',
            fontWeight: 500,
            letterSpacing: '3.2px',
            textTransform: 'uppercase',
            color: GREEN,
            margin: '0 0 22px',
          }}>
            {t('landing_community_aux')}
          </p>
          <h2 style={{
            fontFamily: "'Inter', system-ui, sans-serif",
            fontWeight: 300,
            fontSize: 'clamp(34px, 5vw, 56px)',
            lineHeight: 1.06,
            letterSpacing: '-0.02em',
            margin: '0 0 22px',
            color: GRAFITO,
          }}>
            <strong style={{ fontWeight: 800 }}>{t('landing_community_title_bold')}</strong>
            {t('landing_community_title_rest')}
          </h2>
          <p style={{
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: '18px',
            lineHeight: '24px',
            fontWeight: 400,
            color: 'rgba(39,39,41,0.7)',
            margin: '0 0 26px',
          }}>
            {t('landing_community_subtitle')}
          </p>
          <p style={{
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: '17px',
            lineHeight: 1.55,
            fontWeight: 700,
            color: GRAFITO,
            margin: 0,
          }}>
            {t('landing_community_tagline')}
          </p>
        </div>

        {/* Dark card */}
        <div style={{
          background: GRAFITO,
          borderRadius: '22px',
          padding: '44px 48px',
          boxShadow: '0 30px 70px rgba(0,0,0,.18)',
        }}>
          {/* Two-column body */}
          <div className="grid grid-cols-1 gap-8 min-[700px]:grid-cols-2 min-[700px]:items-start">
            {/* Left column */}
            <div>
              {/* Badge */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                fontFamily: "'DM Mono', monospace",
                fontSize: '12px',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: GREEN,
                border: '1px solid rgba(94,204,128,0.5)',
                borderRadius: '999px',
                padding: '9px 18px',
                marginBottom: '30px',
              }}>
                {t('landing_community_badge')}
              </div>

              {/* Price */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '16px' }}>
                <span style={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: '52px',
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  lineHeight: 1,
                  color: '#fff',
                }}>
                  {t('landing_community_price')}
                </span>
                <span style={{ fontSize: '16px', fontWeight: 500, color: '#9a9a9c' }}>
                  {t('landing_community_period')}
                </span>
              </div>

              {/* Notes */}
              <p style={{ fontSize: '17px', color: '#d7d7d9', margin: '0 0 6px', lineHeight: 1.5 }}>
                {t('landing_community_price_note')}
              </p>
              <p style={{ fontSize: '17px', fontWeight: 700, color: GREEN, margin: 0 }}>
                {t('landing_community_spots')}
              </p>
            </div>

            {/* Right column — features */}
            <ul style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              paddingTop: '8px',
            }}>
              {features.map((key) => (
                <li key={key} style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'flex-start',
                  fontSize: '16px',
                  lineHeight: 1.4,
                  color: '#d7d7d9',
                }}>
                  <Check size={16} style={{ color: GREEN, flexShrink: 0, marginTop: '3px' }} />
                  <BoldInline text={t(key)} />
                </li>
              ))}
            </ul>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.14)', margin: '36px 0 30px' }} />

          {/* Footer */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '30px',
          }}>
            <p style={{ margin: 0, fontSize: '16px', color: '#c2c2c4', lineHeight: 1.5, maxWidth: '560px' }}>
              {t('landing_community_footer_note')}
            </p>
            <Button variant="default" className="min-[600px]:flex-shrink-0 whitespace-nowrap" onClick={() => { trackCtaClicked(t('landing_community_cta'), 'chapter-zero'); scrollToCTA(); }}>
              {t('landing_community_cta')}
            </Button>
          </div>
        </div>

      </div>
    </section>
  );
};
